// src/pages/family/children/ChildProfilePage.tsx
// Main child profile page - renders the layout and default overview tab

import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ChildProfileLayout } from '@/features/children/components/profile/ChildProfileLayout';
import { OverviewTab } from './tabs/OverviewTab';

// Placeholder components for other tabs (to be implemented in Part 4 & 5)
const HealthTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Health Tracking</h2>
    <p className="text-warm-gray">Health tracking features coming in BP-03-04</p>
  </div>
);

const AppointmentsTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Appointments</h2>
    <p className="text-warm-gray">Appointment management coming in BP-05-03</p>
  </div>
);

const MedicationsTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Medications</h2>
    <p className="text-warm-gray">Medication tracking coming in BP-03-04</p>
  </div>
);

const DocumentsTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Documents</h2>
    <p className="text-warm-gray">Document vault coming in BP-04-02</p>
  </div>
);

const GoalsTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Goals & Progress</h2>
    <p className="text-warm-gray">Goal tracking coming in BP-06-03</p>
  </div>
);

const CareTeamTab = () => (
  <div className="bg-white rounded-2xl border border-cream-dark p-8 text-center">
    <h2 className="text-xl font-display font-semibold text-navy mb-2">Care Team</h2>
    <p className="text-warm-gray">Care team management coming in BP-06-03</p>
  </div>
);

/**
 * ChildProfilePage component
 * 
 * Main entry point for child profile views. Wraps content in the
 * ChildProfileLayout and handles routing to different profile tabs.
 * 
 * Routes:
 * - /family/children/:childId - Overview tab (default)
 * - /family/children/:childId/overview - Explicit overview route
 * - /family/children/:childId/health - Health tracking tab
 * - /family/children/:childId/appointments - Appointments tab
 * - /family/children/:childId/medications - Medications tab
 * - /family/children/:childId/documents - Documents tab
 * - /family/children/:childId/goals - Goals tab
 * - /family/children/:childId/care-team - Care team tab
 */
export const ChildProfilePage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();

  if (!childId) {
    return <Navigate to="/family/children" replace />;
  }

  return (
    <ChildProfileLayout>
      <Routes>
        {/* Default to overview */}
        <Route index element={<OverviewTab />} />
        <Route path="overview" element={<OverviewTab />} />
        
        {/* Other tabs - placeholders for now */}
        <Route path="health/*" element={<HealthTab />} />
        <Route path="appointments/*" element={<AppointmentsTab />} />
        <Route path="medications/*" element={<MedicationsTab />} />
        <Route path="documents/*" element={<DocumentsTab />} />
        <Route path="goals/*" element={<GoalsTab />} />
        <Route path="care-team/*" element={<CareTeamTab />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="" replace />} />
      </Routes>
    </ChildProfileLayout>
  );
};

export default ChildProfilePage;
